/** When `true`, pages emit `noindex` robots metadata and `robots.txt` disallows all crawlers. */
export function isNoIndex(): boolean {
  return process.env.NEXT_PUBLIC_NO_INDEX === 'true';
}

/**
 * Master switch for the soft in-app site password gate.
 * Only `true` enables the gate; any other value (including unset) disables it.
 */
export function isSitePasswordEnabled(): boolean {
  return process.env.NEXT_PUBLIC_SITE_PASSWORD_ENABLED === 'true';
}

/** Shared unlock phrase; empty / unset → `null` (gate cannot activate). */
export function getSitePassword(): string | null {
  const value = process.env.NEXT_PUBLIC_SITE_PASSWORD?.trim();
  return value && value.length > 0 ? value : null;
}

/**
 * Embargo end instant as epoch ms from `NEXT_PUBLIC_SITE_PASSWORD_EMBARGO_ENDS_AT`
 * (ISO 8601). Invalid or missing → `null` (gate does not run).
 */
export function getSitePasswordEmbargoEndsAtMs(): number | null {
  const raw = process.env.NEXT_PUBLIC_SITE_PASSWORD_EMBARGO_ENDS_AT?.trim();
  if (!raw) return null;

  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Absolute site origin for metadata URL fields (`openGraph.images`, canonicals, etc.).
 * Prefer an explicit public URL; fall back to Vercel / Netlify / Cloudflare Pages
 * deployment hosts in CI/preview.
 *
 * Vercel vars are hostnames without a protocol; Netlify and Cloudflare Pages vars are
 * absolute URLs.
 * @see https://docs.netlify.com/build/configure-builds/environment-variables/
 * @see https://developers.cloudflare.com/pages/configuration/build-configuration/#environment-variables
 */
export function getSiteOrigin(): URL {
  const explicit = process.env.NEXT_PUBLIC_SITE_URL;
  if (explicit) {
    return new URL(explicit);
  }

  const vercelProductionHost = process.env.VERCEL_PROJECT_PRODUCTION_URL;
  if (vercelProductionHost) {
    return new URL(`https://${vercelProductionHost}`);
  }

  // Netlify `URL` is the production/primary site address (always set on Netlify builds).
  if (process.env.NETLIFY === 'true' && process.env.URL) {
    return new URL(process.env.URL);
  }

  const vercelDeploymentHost = process.env.VERCEL_URL;
  if (vercelDeploymentHost) {
    return new URL(`https://${vercelDeploymentHost}`);
  }

  // Netlify primary URL for this deploy (Deploy Preview, branch deploy, or production).
  if (process.env.NETLIFY === 'true' && process.env.DEPLOY_PRIME_URL) {
    return new URL(process.env.DEPLOY_PRIME_URL);
  }

  // Cloudflare Pages injects CF_PAGES=1 and CF_PAGES_URL (absolute URL for this deploy).
  if (process.env.CF_PAGES && process.env.CF_PAGES_URL) {
    return new URL(process.env.CF_PAGES_URL);
  }

  throw new Error(
    'Missing required environment variable NEXT_PUBLIC_SITE_URL. Add missing variable to continue build'
  );
}
